/**
 * Shopify Variant Handler Service
 * 
 * This service handles variant-related operations for Shopify products,
 * including preparing variant data, handling variant updates, and managing variant images.
 */

export class ShopifyVariantHandlerService {
  /**
   * Constructor for ShopifyVariantHandlerService
   * @param {Object} options - Configuration options
   * @param {Object} options.shopifyService - Shopify service for making API calls
   * @param {Object} options.serviceContainer - Service container for accessing other services
   * @param {string} options.shopifyLocationId - Default Shopify location ID
   */
  constructor(options = {}) {
    this.shopifyService = options.shopifyService;
    this.serviceContainer = options.serviceContainer;
    this.cachedLocationId = null;
    
    if (!this.shopifyService && !this.serviceContainer) {
      console.warn('ShopifyVariantHandlerService initialized without shopifyService or serviceContainer. API calls will fail unless provided in method calls.');
    }
  }

  /**
   * Prepares variant data for API submission
   * @param {Object} variant - Original variant data
   * @param {Object} variantUpdates - UI updates for the variant
   * @param {Array} productOptions - Product options
   * @param {Object} productInput - Product input data
   * @returns {Promise<Object>} Prepared variant data
   */
  async prepareVariantData(variant, variantUpdates, productOptions, productInput = {}) {
    console.log(`Preparing variant data for ${variant.id || 'new variant'}`);
    
    // Merge variant with updates
    const mergedVariant = { ...variant, ...variantUpdates };
    
    // Format option values
    const formattedOptions = this.formatOptionValues(mergedVariant, productOptions);
    console.log(`Formatted option values: ${JSON.stringify(formattedOptions)}`);
    
    // Get price and compare at price
    let price = mergedVariant.price || '0.00';
    if (typeof price !== 'string') {
      price = String(price);
    }
    
    let compareAtPrice = mergedVariant.compare_at_price || mergedVariant.compareAtPrice || '';
    if (compareAtPrice && typeof compareAtPrice !== 'string') {
      compareAtPrice = String(compareAtPrice);
    }
    
    // Get SKU
    const sku = mergedVariant.sku || 
               (mergedVariant.inventoryItem && mergedVariant.inventoryItem.sku) || 
               (mergedVariant.inventoryItem && mergedVariant.inventoryItem.sku) || 
               '';
    
    // Get inventory quantity
    let inventoryQuantity = 0;
    const quantityValue = mergedVariant.inventoryQuantity || 
                        mergedVariant.inventory_quantity || 
                        mergedVariant.quantity || 
                        (mergedVariant.inventory && mergedVariant.inventory.quantity) ||
                        (mergedVariant.inventoryItem && mergedVariant.inventoryItem.quantity) ||
                        (mergedVariant.inventoryItem && mergedVariant.inventoryItem.quantity);
    
    if (quantityValue !== undefined) {
      inventoryQuantity = parseInt(quantityValue, 10);
      if (isNaN(inventoryQuantity)) {
        console.warn(`Invalid inventory quantity value: ${quantityValue}, defaulting to 0`);
        inventoryQuantity = 0;
      }
    }
    
    // Get image
    const image = mergedVariant.image || null;
    
    // Get location ID
    const locationId = await this.getShopifyLocationId();
    
    // Prepare the variant data
    const variantData = {
      optionValues: formattedOptions.map(option => ({
        name: option.value,
        optionName: option.name
      })),
      price,
      compareAtPrice,
      inventoryPolicy: "DENY",
      inventoryQuantities: [
        { locationId: locationId, availableQuantity: inventoryQuantity }
      ],
      inventoryItem: { sku },
      imageId: image?.id || null
    };
    
    return variantData;
  }

  /**
   * Format option values for a variant
   * @param {Object} variant - The variant object
   * @param {Array} options - Product options
   * @returns {Array} Formatted option values as objects with name and value properties
   */
  formatOptionValues(variant, options) {
    console.log('Formatting option values for variant');
    let formattedOptions = [];
    
    // Check for options array format
    if (variant.options && Array.isArray(variant.options) && variant.options.length > 0) {
      console.log('Using variant.options for option values');
      
      formattedOptions = variant.options.map((option, index) => {
        const optionName = options && options[index] ? options[index].name : `Option ${index + 1}`;
        const optionValue = typeof option === 'object' ? 
          (option.name || option.value || '') : 
          String(option || '');
        
        // Store original value for preservation
        return {
          name: optionName,
          value: optionValue,
          originalValue: optionValue // Store original case
        };
      }).filter(option => option.value !== undefined && option.value !== null && option.value !== '');
      
      console.log('Formatted option values from options:', JSON.stringify(formattedOptions));
      if (formattedOptions.length > 0) {
        return formattedOptions;
      }
    }
    
    // Check for option1, option2, option3 format
    if (variant.option1 !== undefined) {
      console.log('Using option1, option2, option3 for option values');
      
      formattedOptions = [];
      
      // Add each option if it exists
      if (variant.option1 !== undefined && variant.option1 !== null && variant.option1 !== '') {
        formattedOptions.push({
          name: options && options[0] ? options[0].name : 'Option 1',
          value: variant.option1,
          originalValue: variant.option1 // Store original case
        });
      }
      
      if (variant.option2 !== undefined && variant.option2 !== null && variant.option2 !== '') {
        formattedOptions.push({
          name: options && options[1] ? options[1].name : 'Option 2',
          value: variant.option2,
          originalValue: variant.option2 // Store original case
        });
      }
      
      if (variant.option3 !== undefined && variant.option3 !== null && variant.option3 !== '') {
        formattedOptions.push({
          name: options && options[2] ? options[2].name : 'Option 3',
          value: variant.option3,
          originalValue: variant.option3 // Store original case
        });
      }
      
      console.log('Formatted option values from option1, option2, option3:', JSON.stringify(formattedOptions));
      if (formattedOptions.length > 0) {
        return formattedOptions;
      }
    }
    
    // Check for selectedOptions format (from Shopify API)
    if (variant.selectedOptions && Array.isArray(variant.selectedOptions) && variant.selectedOptions.length > 0) {
      console.log('Using selectedOptions for option values');
      
      formattedOptions = variant.selectedOptions.map((selectedOption) => {
        return {
          name: selectedOption.name || 'Option',
          value: selectedOption.value || '',
          originalValue: selectedOption.value || '' // Store original case
        };
      }).filter(option => option.value !== undefined && option.value !== null && option.value !== '');
      
      console.log('Formatted option values from selectedOptions:', JSON.stringify(formattedOptions));
      if (formattedOptions.length > 0) {
        return formattedOptions;
      }
    }
    
    // Check for optionValues array format
    if (variant.optionValues && Array.isArray(variant.optionValues) && variant.optionValues.length > 0) {
      console.log('Using variant.optionValues for option values');
      
      // If optionValues already has the correct format, return it directly
      if (typeof variant.optionValues[0] === 'object' && 
          variant.optionValues[0] !== null) {
        
        formattedOptions = variant.optionValues.map((option, index) => {
          // If it's already in the Shopify API format with name and value properties, use it directly
          if (option.name && option.value) {
            return option;
          }
          
          // If it has option and value fields, convert to name and optionName
          if (option.option && option.value) {
            return {
              name: option.value,
              optionName: option.option
            };
          }
          
          // Get the option name from the product options or use a default
          const optionName = option.optionName || 
                           (options && options[index] ? options[index].name : `Option ${index + 1}`);
          
          // Get the option value from the option object
          const optionValue = option.name || option.value || '';
          
          // Store original value for preservation
          return {
            name: optionName,
            value: optionValue,
            originalValue: optionValue // Store original case
          };
        }).filter(option => option.value !== undefined && option.value !== null && option.value !== '');
        
        console.log('Formatted option values from optionValues objects:', JSON.stringify(formattedOptions));
        if (formattedOptions.length > 0) {
          return formattedOptions;
        }
      } else if (typeof variant.optionValues[0] === 'string') {
        // If optionValues is an array of strings, convert to objects
        formattedOptions = variant.optionValues.map((value, index) => {
          return {
            name: options && options[index] ? options[index].name : `Option ${index + 1}`,
            value: value,
            originalValue: value // Store original case
          };
        }).filter(option => option.value !== undefined && option.value !== null && option.value !== '');
        
        console.log('Formatted option values from optionValues strings:', JSON.stringify(formattedOptions));
        if (formattedOptions.length > 0) {
          return formattedOptions;
        }
      }
    }
    
    // If we reach here, we couldn't find option values in any expected format
    // Try to create default option values based on the options provided
    if (options && Array.isArray(options) && options.length > 0) {
      console.log('Creating default option values from product options');
      
      // Create default option values using the first value of each option
      formattedOptions = options.map(option => {
        let defaultValue = '';
        
        if (option.values && option.values.length > 0) {
          defaultValue = typeof option.values[0] === 'string' ? 
            option.values[0] : 
            (option.values[0].name || option.values[0].value || '');
        }
        
        // Store original value for preservation
        return {
          name: option.name || `Option ${option.position || 1}`,
          value: defaultValue,
          originalValue: defaultValue // Store original case
        };
      }).filter(option => option.value !== undefined && option.value !== null && option.value !== '');
      
      console.log('Created default option values:', JSON.stringify(formattedOptions));
      if (formattedOptions.length > 0) {
        return formattedOptions;
      }
    }
    
    // If all else fails, create a minimal default option value to prevent errors
    console.warn('Could not determine option values for variant, creating minimal default');
    if (options && Array.isArray(options) && options.length > 0) {
      // Create a minimal option value for the first option
      formattedOptions = [{
        name: options[0].name || 'Option 1',
        value: 'Default',
        originalValue: 'Default' // Store original case
      }];
      return formattedOptions;
    } else {
      // If we don't even have options, create a truly minimal default
      formattedOptions = [{
        name: 'Option 1',
        value: 'Default',
        originalValue: 'Default' // Store original case
      }];
      return formattedOptions;
    }
  }

  /**
   * Validates and cleans option values to ensure they are properly formatted
   * @param {Array} optionValues - The option values to validate
   * @param {Array} productOptions - The product options
   * @returns {Array} Validated and cleaned option values as objects with name and value properties
   * @private
   */
  validateAndCleanOptionValues(optionValues, productOptions) {
    if (!optionValues || !Array.isArray(optionValues)) {
      console.warn('Invalid option values provided to validateAndCleanOptionValues');
      return [];
    }
    
    console.log('Validating and cleaning option values:', JSON.stringify(optionValues));
    
    // Handle case where optionValues is an array of strings (direct values)
    if (optionValues.length > 0 && typeof optionValues[0] === 'string') {
      console.log('Converting string option values to objects with name/value properties');
      return optionValues.map((value, index) => {
        return {
          name: productOptions && productOptions[index] ? productOptions[index].name : `Option ${index + 1}`,
          value: value
        };
      }).filter(option => option.value !== undefined && option.value !== null && option.value !== '');
    }
    
    // Filter out invalid option values
    const validOptionValues = optionValues.filter(option => 
      option && 
      typeof option === 'object' && 
      (option.name !== undefined || option.value !== undefined)
    );
    
    // If we have no valid option values, return an empty array
    if (validOptionValues.length === 0) {
      console.warn('No valid option values found after filtering');
      return [];
    }
    
    // Map option values to the format expected by our internal methods (objects with name and value)
    const formattedOptionValues = validOptionValues.map((option, index) => {
      // If the option is already in the correct format (has both name and value), return it
      if (option.name && option.value) {
        return option;
      }
      
      // Get the option name from the product options or use a default
      const optionName = option.optionName || 
                         (productOptions && productOptions[index] ? productOptions[index].name : `Option ${index + 1}`);
      
      // Get the option value from the option object
      const optionValue = option.name || option.value || '';
      
      // Return the option in the format expected by our internal methods
      return {
        name: optionName,
        value: optionValue
      };
    }).filter(option => option.value !== undefined && option.value !== null && option.value !== '');
    
    console.log('Validated and cleaned option values:', JSON.stringify(formattedOptionValues));
    return formattedOptionValues;
  }

  /**
   * Calculate option values based on variant index and product options
   * @param {number} variantIndex - The index of the variant in the variants array
   * @param {Array} productOptions - The product options
   * @returns {Array} Calculated option values
   */
  calculateOptionValuesFromIndex(variantIndex, productOptions) {
    if (variantIndex === -1 || !productOptions || !Array.isArray(productOptions)) {
      return [];
    }
    
    // Get the number of values for each option
    const optionValueCounts = productOptions.map(option => {
      return option.values && Array.isArray(option.values) ? option.values.length : 0;
    });
    
    // If any option has no values, we can't calculate
    if (optionValueCounts.includes(0)) {
      console.warn('Cannot calculate option values: some options have no values');
      return [];
    }
    
    // Calculate the option values based on the variant index
    const optionValues = [];
    let remainingIndex = variantIndex;
    
    // Work backwards through the options
    for (let i = optionValueCounts.length - 1; i >= 0; i--) {
      const valueCount = optionValueCounts[i];
      const valueIndex = remainingIndex % valueCount;
      
      // Get the option value at this index
      const option = productOptions[i];
      const value = option.values[valueIndex];
      const optionValue = typeof value === 'string' ? value : value.name || value.value;
      
      // Add to the beginning of the array (since we're working backwards)
      optionValues.unshift(optionValue);
      
      // Update the remaining index for the next iteration
      remainingIndex = Math.floor(remainingIndex / valueCount);
    }
    
    return optionValues;
  }

  /**
   * Get the index of a variant in the variants array
   * @param {Object} variant - The variant to find
   * @param {Object} productInput - The product input data
   * @returns {number} The index of the variant, or -1 if not found
   */
  getVariantIndex(variant, productInput = {}) {
    if (!variant) {
      return -1;
    }
    
    // Get the variant ID
    const variantId = variant.id?.toString() || variant.variant_id?.toString() || '';
    
    // If we have a variant ID and productInput has variants, try to find the index
    if (variantId && productInput.variants && Array.isArray(productInput.variants)) {
      const index = productInput.variants.findIndex(v => {
        const vId = v.id?.toString() || v.variant_id?.toString() || '';
        return vId === variantId;
      });
      
      if (index !== -1) {
        console.log(`Found variant ${variantId} at index ${index} in productInput.variants`);
        return index;
      }
    }
    
    // If the variant has a position property, use that (subtract 1 because positions are 1-indexed)
    if (variant.position) {
      const index = parseInt(variant.position, 10) - 1;
      console.log(`Using variant.position to determine index: ${index}`);
      return index;
    }
    
    // If we couldn't find the index, return -1
    console.warn(`Could not determine index for variant ${variantId}`);
    return -1;
  }

  /**
   * Parse inventory quantity from a variant object
   * @param {Object} variant - The variant object
   * @returns {number} The parsed inventory quantity
   */
  parseInventoryQuantity(variant) {
    if (!variant) {
      console.warn('No variant provided to parseInventoryQuantity');
      return 0;
    }
    
    let quantity = null;
    
    // First check for UI updates in the variant
    if (variant.inventoryQuantity !== undefined) {
      quantity = variant.inventoryQuantity;
      console.log(`Using inventory quantity from UI updates (inventoryQuantity): ${quantity}`);
    } else if (variant.inventory_quantity !== undefined) {
      quantity = variant.inventory_quantity;
      console.log(`Using inventory quantity from UI updates (inventory_quantity): ${quantity}`);
    }
    // Then check all possible inventory quantity properties in the variant
    else if (variant.quantity !== undefined) {
      quantity = variant.quantity;
      console.log(`Using inventory quantity from variant.quantity: ${quantity}`);
    } else if (variant.inventory && variant.inventory.quantity !== undefined) {
      quantity = variant.inventory.quantity;
      console.log(`Using inventory quantity from variant.inventory.quantity: ${quantity}`);
    } else if (variant.inventoryItem && variant.inventoryItem.inventoryQuantity !== undefined) {
      quantity = variant.inventoryItem.inventoryQuantity;
      console.log(`Using inventory quantity from inventoryItem.inventoryQuantity: ${quantity}`);
    } else if (variant.inventoryItem && variant.inventoryItem.inventory_quantity !== undefined) {
      quantity = variant.inventoryItem.inventory_quantity;
      console.log(`Using inventory quantity from inventoryItem.inventory_quantity: ${quantity}`);
    }
    
    // Parse the quantity to an integer
    const parsedQuantity = parseInt(quantity, 10);
    
    // Return 0 if the quantity is not a valid number
    if (isNaN(parsedQuantity) || parsedQuantity < 0) {
      console.warn(`Invalid inventory quantity value: ${quantity}, defaulting to 0`);
      return 0;
    }
    
    return parsedQuantity;
  }

  /**
   * Process variant updates from local edits
   * @param {Object} productInput - The product input data
   * @param {Object} variant - The variant to process updates for
   * @returns {Object} The updated variant data
   */
  processVariantUpdates(productInput, variant) {
    const variantId = variant.id?.toString() || variant.variant_id?.toString() || '';
    const variantUpdates = (productInput && productInput.variant_updates && variantId) ? 
      productInput.variant_updates[variantId] : {};
    
    if (Object.keys(variantUpdates).length > 0) {
      console.log(`Processing updates for variant ${variantId}:`, variantUpdates);
      
      // Apply updates to the variant
      Object.entries(variantUpdates).forEach(([field, value]) => {
        variant[field] = value;
      });
    }
    
    return variant;
  }

  /**
   * Prepares variant input for the Shopify API
   * @param {Object} variant - Variant to prepare
   * @param {string} productId - Shopify product ID
   * @param {Array} productOptions - Product options
   * @returns {Promise<Object>} Prepared variant input
   */
  async prepareVariantInput(variant, productId, productOptions = []) {
    // Check if variant is an array - this is a safeguard to prevent processing an array of variants
    if (Array.isArray(variant)) {
      console.error('prepareVariantInput was called with an array of variants instead of a single variant. This is likely a bug.');
      console.log('Variant array:', JSON.stringify(variant));
      // Return an empty object to prevent further errors
      return {};
    }
    
    console.log(`Preparing variant input for variant with SKU ${variant.sku || variant.inventoryItem?.sku || 'unknown'}`);
    
    // Ensure SKU is set
    let sku = variant.sku || '';
    if (!sku && variant.inventoryItem && variant.inventoryItem.sku) {
      sku = variant.inventoryItem.sku;
      console.log(`Using SKU from inventoryItem: ${sku}`);
    }
    
    // Properly handle price and compareAtPrice
    let price = variant.price || '0.00';
    if (typeof price !== 'string') {
      price = String(price);
    }
    
    // Prioritize compare_at_price from UI updates, then check other possible sources
    let compareAtPrice = variant.compare_at_price || variant.compareAtPrice || null;
    if (compareAtPrice && typeof compareAtPrice !== 'string') {
      compareAtPrice = String(compareAtPrice);
    }
    if (compareAtPrice === '') {
      compareAtPrice = null;
    }
    
    console.log(`Using price: ${price} and compareAtPrice: ${compareAtPrice}`);
    
    // Create the variant input
    const variantInput = {
      title: variant.title || '',
      price: price,
      compareAtPrice: compareAtPrice,
      inventoryQuantities: [],
      inventoryItem: {
        sku: sku // Set SKU directly in the variant input
      }
    };
    
    // Carry through tracked setting if present
    if (variant.tracked !== undefined && variant.tracked !== null) {
      variantInput.tracked = variant.tracked;
      console.log(`Carrying through tracked=${variant.tracked} in prepareVariantInput`);
    }
    
    // Carry through inventory_policy / inventoryPolicy if present
    const policy = variant.inventory_policy || variant.inventoryPolicy;
    if (policy) {
      variantInput.inventory_policy = policy;
      variantInput.inventoryPolicy = policy;
      console.log(`Carrying through inventory_policy=${policy} in prepareVariantInput`);
    }
    
    // Carry through taxable setting if present
    if (variant.taxable !== undefined && variant.taxable !== null) {
      variantInput.taxable = variant.taxable;
      console.log(`Carrying through taxable=${variant.taxable} in prepareVariantInput`);
    }
    
    // Add inventory quantities if available
    if (variant.inventoryQuantities && Array.isArray(variant.inventoryQuantities) && 
        variant.inventoryQuantities.length > 0) {
      variantInput.inventoryQuantities = variant.inventoryQuantities;
      console.log(`Using existing inventoryQuantities: ${JSON.stringify(variantInput.inventoryQuantities)}`);
    } else if (variant.inventoryQuantity !== undefined) {
      // If we have a single inventory quantity, use it
      const locationId = await this.getShopifyLocationId();
      variantInput.inventoryQuantities = [{
        availableQuantity: variant.inventoryQuantity,
        locationId
      }];
      console.log(`Created inventoryQuantities from inventoryQuantity: ${variant.inventoryQuantity}`);
    }
    
    // Extract raw option values from various sources
    let rawOptionValues = [];
    
    // Get the actual option names from the product options
    const actualOptionNames = [];
    if (productOptions && Array.isArray(productOptions)) {
      for (let i = 0; i < productOptions.length; i++) {
        if (productOptions[i] && productOptions[i].name) {
          actualOptionNames[i] = productOptions[i].name;
        }
      }
    }
    console.log(`Using actual option names from product: ${JSON.stringify(actualOptionNames)}`);
    
    // If we don't have any actual option names but we do have option1_name, option2_name, etc. in the product data
    // Try to get them from the product object directly
    if (actualOptionNames.length === 0 && variant.product) {
      console.log('No option names found in productOptions, checking product object');
      if (variant.product.option1_name) actualOptionNames[0] = variant.product.option1_name;
      if (variant.product.option2_name) actualOptionNames[1] = variant.product.option2_name;
      if (variant.product.option3_name) actualOptionNames[2] = variant.product.option3_name;
      console.log(`Found option names in product object: ${JSON.stringify(actualOptionNames)}`);
    }
    
    if (variant.optionValues && Array.isArray(variant.optionValues) && variant.optionValues.length > 0) {
      // Use the existing option values
      variantInput.optionValues = variant.optionValues.map((ov, index) => {
        // If it's already in the correct format, use it directly
        if (typeof ov === 'object' && (ov.name || ov.value) && (ov.optionId || ov.optionName)) {
          // Use the actual option names from the product options if available
          const optionName = actualOptionNames[index] || `Option${index + 1}`;
          
          return {
            name: ov.name || ov.value,
            optionName: optionName
          };
        }
        
        // Otherwise, format it correctly
        const value = typeof ov === 'string' ? ov : (ov.value || ov.name || '');
        const optionName = actualOptionNames[index] || `Option${index + 1}`;
        
        return {
          name: value,
          optionName: optionName
        };
      });
      console.log(`Set optionValues in variantInput: ${JSON.stringify(variantInput.optionValues)}`);
    } else if (variant.options && Array.isArray(variant.options)) {
      // Convert options to optionValues
      variantInput.optionValues = variant.options.map((option, index) => {
        const optionName = actualOptionNames[index] || `Option${index + 1}`;
        
        return {
          name: option,
          optionName: optionName
        };
      });
      console.log(`Converted options to optionValues: ${JSON.stringify(variantInput.optionValues)}`);
    } else if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
      // Convert selectedOptions to optionValues
      variantInput.optionValues = variant.selectedOptions.map((so, index) => {
        const optionName = actualOptionNames[index] || `Option${index + 1}`;
        
        return {
          name: so.value,
          optionName: optionName
        };
      });
      console.log(`Converted selectedOptions to optionValues: ${JSON.stringify(variantInput.optionValues)}`);
    }
    
    console.log(`Prepared variant input: ${JSON.stringify(variantInput)}`);
    return variantInput;
  }

  /**
   * Formats option values for the Shopify API
   * @param {Array} optionValues - Array of option values with name and optionName
   * @param {Array} productOptions - Array of product options
   * @returns {Array} - Array of option values in the correct order for the Shopify API
   */
  formatOptionValuesForAPI(optionValues, productOptions) {
    console.log(`Formatting option values for API: ${JSON.stringify(optionValues)}`);
    console.log(`Product options: ${JSON.stringify(productOptions)}`);
    
    // Handle null or empty option values
    if (!optionValues || !Array.isArray(optionValues) || optionValues.length === 0) {
      console.warn('No option values provided for formatting');
      
      // If we have product options, create default values based on them
      if (productOptions && Array.isArray(productOptions) && productOptions.length > 0) {
        console.log('Creating default option values from product options');
        // Return the first value of each option as a default
        return productOptions.map(option => {
          if (option.values && option.values.length > 0) {
            return typeof option.values[0] === 'string' ? option.values[0] : 
                   (option.values[0].name || option.values[0].value || 'Default');
          }
          return 'Default';
        });
      }
      
      return [];
    }
    
    // If all option values are already strings and match the number of product options,
    // we can assume they're already in the correct order
    if (productOptions && 
        Array.isArray(productOptions) && 
        optionValues.length === productOptions.length && 
        optionValues.every(ov => typeof ov === 'string')) {
      console.log('Option values are already strings in the correct order');
      return optionValues;
    }
    
    // If we don't have product options, extract values as best we can
    if (!productOptions || !Array.isArray(productOptions) || productOptions.length === 0) {
      console.warn('No product options provided for formatting');
      // Return just the values as strings
      return optionValues.map(ov => {
        if (typeof ov === 'string') return ov;
        if (ov === null || ov === undefined) return 'Default';
        return ov.name || ov.value || 'Default';
      });
    }
    
    // Create an array of option values in the same order as the product options
    const formattedValues = [];
    
    // First try to match by option name
    for (const productOption of productOptions) {
      // Find the matching option value
      const matchingOptionValue = optionValues.find(ov => {
        if (typeof ov === 'string') return false; // Skip string values for name matching
        
        // Try to match by optionName or option property
        if (ov.optionName && productOption.name) {
          return ov.optionName.toLowerCase() === productOption.name.toLowerCase();
        }
        if (ov.option && productOption.name) {
          return ov.option.toLowerCase() === productOption.name.toLowerCase();
        }
        if (ov.name && productOption.name && typeof ov.name === 'string') {
          // This might be the option name, not the value
          return ov.name.toLowerCase() === productOption.name.toLowerCase();
        }
        return false;
      });
      
      if (matchingOptionValue) {
        // Extract the value from the matching option value
        const value = typeof matchingOptionValue === 'string' 
          ? matchingOptionValue 
          : (matchingOptionValue.value || matchingOptionValue.name || 'Default');
        
        formattedValues.push(value);
        console.log(`Matched option value by name: ${value} for option ${productOption.name}`);
      } else {
        // If we can't find a match by name, try to use the option value at the same position
        if (formattedValues.length < optionValues.length) {
          const positionValue = optionValues[formattedValues.length];
          const value = typeof positionValue === 'string' 
            ? positionValue 
            : (positionValue.value || positionValue.name || 'Default');
          
          formattedValues.push(value);
          console.log(`Using option value by position: ${value} for option ${productOption.name}`);
        } else {
          // If we don't have enough option values, use a default value
          const defaultValue = productOption.values && productOption.values.length > 0 
            ? (typeof productOption.values[0] === 'string' ? productOption.values[0] : 
               (productOption.values[0].name || productOption.values[0].value || 'Default')) 
            : 'Default';
          
          formattedValues.push(defaultValue);
          console.log(`Using default value: ${defaultValue} for option ${productOption.name}`);
        }
      }
    }
    
    console.log(`Formatted option values: ${JSON.stringify(formattedValues)}`);
    return formattedValues;
  }

  /**
   * Creates product variants using the Shopify GraphQL API
   * @param {string} productId - Shopify product ID
   * @param {Array} variants - Variants to create
   * @param {Object} productInput - Product input data
   * @returns {Promise<Object>} Created variants
   */
  async createProductVariants(productId, variants, productInput = {}) {
    console.log(`Creating ${variants.length} variants for product ${productId}`);
    
    // Get the actual option names directly from the product
    const actualOptionNames = await this.getProductOptionNames(productId);
    console.log(`Retrieved actual option names from product: ${JSON.stringify(actualOptionNames)}`);
    
    // Validate that variants is an array
    if (!Array.isArray(variants)) {
      console.error('createProductVariants was called with a non-array variants parameter');
      console.log('Variants:', variants);
      return { variants: [] };
    }
    
    // Create a case mapping for option values
    const caseMapping = {};
    if (productInput.options && Array.isArray(productInput.options)) {
      productInput.options.forEach(option => {
        if (option.values && Array.isArray(option.values)) {
          caseMapping[option.name] = option.values.map(value => {
            const valueStr = typeof value === 'string' ? value : (value.name || value.value || '');
            return [valueStr.toLowerCase(), valueStr];
          });
        }
      });
    }
    
    // Correct the variants for the API
    const correctedVariants = variants.map(variant => {
      // Create a copy of the variant to avoid modifying the original
      const correctedVariant = { ...variant };
      
      // Ensure option values are in the correct format
      if (correctedVariant.optionValues && Array.isArray(correctedVariant.optionValues)) {
        // Ensure each option value has the required fields
        correctedVariant.optionValues = correctedVariant.optionValues.map((value, index) => {
          // If the value is already in the correct format, use it
          if (typeof value === 'object' && value.name && value.optionName) {
            // Make sure to use the actual option names from the product
            return {
              name: value.name,
              optionName: actualOptionNames[index] || value.optionName
            };
          }
          
          // If the value is a string, convert it to an object
          const optionValue = typeof value === 'string' ? value : (value.name || value.value || '');
          
          // Use the actual option names from the product
          return {
            name: optionValue,
            optionName: actualOptionNames[index] || `Option${index + 1}`
          };
        });
      }
      
      // Apply correct casing if available
      if (caseMapping && correctedVariant.optionValues && productInput && productInput.options && Array.isArray(productInput.options)) {
        correctedVariant.optionValues = correctedVariant.optionValues.map((value, index) => {
          if (index < productInput.options.length) {
            const optionName = productInput.options[index].name;
            if (caseMapping[optionName]) {
              const correctCase = caseMapping[optionName].find(
                ([lower, original]) => lower === value.name.toLowerCase()
              );
              
              if (correctCase) {
                console.log(`Using correctly cased value '${correctCase[1]}' instead of '${value.name}' for option '${optionName}'`);
                return {
                  name: correctCase[1],
                  optionName: optionName
                };
              }
            }
          }
          return value;
        });
      }
      
      // Ensure SKU is set properly
      if (!correctedVariant.sku && correctedVariant.inventoryItem && correctedVariant.inventoryItem.sku) {
        correctedVariant.sku = correctedVariant.inventoryItem.sku;
      }
      
      // Ensure price and compareAtPrice are properly formatted
      if (correctedVariant.price && typeof correctedVariant.price !== 'string') {
        correctedVariant.price = String(correctedVariant.price);
      }
      
      // Handle compareAtPrice from different possible sources
      let compareAtPrice = correctedVariant.compare_at_price || correctedVariant.compareAtPrice || null;
      if (compareAtPrice && typeof compareAtPrice !== 'string') {
        compareAtPrice = String(compareAtPrice);
      }
      if (compareAtPrice === '') {
        compareAtPrice = null;
      }
      correctedVariant.compareAtPrice = compareAtPrice;
      console.log(`Using compareAtPrice: ${compareAtPrice}`); // Added logging for compareAtPrice
      return correctedVariant;
    });
    
    // GraphQL query for bulk variant creation
    const query = `
      mutation productVariantsBulkCreate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
        productVariantsBulkCreate(productId: $productId, variants: $variants) {
          productVariants {
            id
            title
            price
            compareAtPrice
            inventoryQuantity
            selectedOptions {
              name
              value
            }
            inventoryItem {
              id
              sku
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;
    
    // Get the Shopify service
    const shopifyService = this.shopifyService || this.serviceContainer.get('ShopifyGraphQLService');
    
    // Convert the variants to the format expected by the API
    const bulkVariants = await Promise.all(correctedVariants.map(async (variant) => {
      // Ensure each variant has the required fields
      const inventoryItemInput = {
        sku: variant.sku || (variant.inventoryItem && variant.inventoryItem.sku) || ''
      };
      
      // Only add tracked if explicitly set (avoid sending undefined to API)
      if (variant.tracked !== undefined && variant.tracked !== null) {
        inventoryItemInput.tracked = (variant.tracked === 'true' || variant.tracked === true);
        console.log(`Setting tracked=${inventoryItemInput.tracked} for variant with SKU ${inventoryItemInput.sku}`);
      }
      
      const variantInput = {
        optionValues: [], 
        price: variant.price || '0.00',
        compareAtPrice: variant.compareAtPrice || variant.compare_at_price || null,
        inventoryQuantities: [],
        inventoryItem: inventoryItemInput
      };
      
      // Add inventoryPolicy if set (from InventoryTools)
      const policy = variant.inventory_policy || variant.inventoryPolicy;
      if (policy) {
        variantInput.inventoryPolicy = policy.toUpperCase();
        console.log(`Setting inventoryPolicy=${variantInput.inventoryPolicy} for variant with SKU ${variant.sku}`);
      }
      
      // Add taxable if explicitly set (from InventoryTools)
      if (variant.taxable !== undefined && variant.taxable !== null) {
        variantInput.taxable = (variant.taxable === 'true' || variant.taxable === true);
        console.log(`Setting taxable=${variantInput.taxable} for variant with SKU ${variant.sku}`);
      }
      
      console.log(`Variant input with price: ${variantInput.price} and compareAtPrice: ${variantInput.compareAtPrice}`);
      
      // Add inventory quantities if available
      if (variant.inventoryQuantities && Array.isArray(variant.inventoryQuantities) && 
          variant.inventoryQuantities.length > 0) {
        variantInput.inventoryQuantities = variant.inventoryQuantities;
        console.log(`Using existing inventoryQuantities: ${JSON.stringify(variantInput.inventoryQuantities)}`);
      } else if (variant.inventoryQuantity !== undefined) {
        // If we have a single inventory quantity, use it
        const locationId = await this.getShopifyLocationId();
        variantInput.inventoryQuantities = [{
          availableQuantity: variant.inventoryQuantity,
          locationId
        }];
        console.log(`Created inventoryQuantities from inventoryQuantity: ${variant.inventoryQuantity}`);
      }
      
      // Extract raw option values from various sources
      let rawOptionValues = [];
      
      if (variant.optionValues && Array.isArray(variant.optionValues) && variant.optionValues.length > 0) {
        // Use the existing option values
        variantInput.optionValues = variant.optionValues.map((ov, index) => {
          // If it's already in the correct format, use it directly
          if (typeof ov === 'object' && (ov.name || ov.value) && (ov.optionId || ov.optionName)) {
            return {
              name: ov.name || ov.value,
              optionName: ov.optionName || actualOptionNames[index] || (productInput.options && productInput.options[index] && productInput.options[index].name) || `Option${index + 1}`
            };
          }
          
          // Otherwise, format it correctly
          const value = typeof ov === 'string' ? ov : (ov.value || ov.name || '');
          return {
            name: value,
            optionName: actualOptionNames[index] || (productInput.options && productInput.options[index] && productInput.options[index].name) || `Option${index + 1}`
          };
        });
        console.log(`Set optionValues in variantInput: ${JSON.stringify(variantInput.optionValues)}`);
      } else if (variant.options && Array.isArray(variant.options)) {
        // Convert options to optionValues
        variantInput.optionValues = variant.options.map((option, index) => {
          return {
            name: option,
            optionName: actualOptionNames[index] || (productInput.options && productInput.options[index] && productInput.options[index].name) || `Option${index + 1}`
          };
        });
        console.log(`Converted options to optionValues: ${JSON.stringify(variantInput.optionValues)}`);
      } else if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
        // Convert selectedOptions to optionValues
        variantInput.optionValues = variant.selectedOptions.map((so, index) => {
          return {
            name: so.value,
            optionName: actualOptionNames[index] || (productInput.options && productInput.options[index] && productInput.options[index].name) || `Option${index + 1}`
          };
        });
        console.log(`Converted selectedOptions to optionValues: ${JSON.stringify(variantInput.optionValues)}`);
      }
      
      console.log(`Final bulk variant input: ${JSON.stringify(variantInput)}`);
      return variantInput;
    }));
    
    // Format the variables for the API request
    const variables = {
      productId,
      variants: bulkVariants
    };
    
    // Log the SKUs and prices in the final request to verify they're being sent correctly
    console.log(`Sending ${bulkVariants.length} variants to Shopify with SKUs:`, 
      bulkVariants.map(v => v.inventoryItem.sku).join(', '));
    console.log(`Variant prices:`, bulkVariants.map(v => v.price).join(', '));
    console.log(`Variant compareAtPrices:`, bulkVariants.map(v => v.compareAtPrice || 'null').join(', '));
    
    try {
      // Make the API request
      const response = await shopifyService.makeGraphQLRequest({
        query,
        variables
      });
      
      // Check for errors
      if (response && response.productVariantsBulkCreate) {
        if (response.productVariantsBulkCreate.userErrors && 
            response.productVariantsBulkCreate.userErrors.length > 0) {
          console.error('Errors creating variants:', JSON.stringify(response.productVariantsBulkCreate.userErrors));
          console.log('Errors detected, attempting to create variants individually');
          return this._createVariantsIndividually(productId, correctedVariants, productInput);
        }
        
        // Log success information
        if (response.productVariantsBulkCreate.productVariants) {
          const createdVariants = response.productVariantsBulkCreate.productVariants;
          console.log(`Successfully created ${createdVariants.length} variants`);
          
          // Return the created variants
          return {
            variants: createdVariants
          };
        } else {
          console.error('Error creating variants: No variants returned in response');
          return this._createVariantsIndividually(productId, correctedVariants, productInput);
        }
      } else {
        console.error('Error creating variants: Invalid response format');
        return this._createVariantsIndividually(productId, correctedVariants, productInput);
      }
    } catch (error) {
      console.error('Error creating variants in bulk:', error);
      return this._createVariantsIndividually(productId, correctedVariants, productInput);
    }
  }
  
  /**
   * Creates variants individually when bulk creation fails
   * @param {string} productId - Shopify product ID
   * @param {Array} variants - Variants to create
   * @param {Object} productInput - Product input data
   * @returns {Promise<Object>} Created variants
   * @private
   */
  async _createVariantsIndividually(productId, variants, productInput = {}) {
    console.log(`Creating ${variants.length} variants individually for product ${productId}`);
    
    // Get the actual option names directly from the product
    const actualOptionNames = await this.getProductOptionNames(productId);
    console.log(`Retrieved actual option names for individual creation: ${JSON.stringify(actualOptionNames)}`);
    
    const createdVariants = [];
    const shopifyService = this.shopifyService || this.serviceContainer.get('ShopifyGraphQLService');
    
    for (const variant of variants) {
      try {
        // Use prepareVariantInput to ensure consistent formatting
        const preparedVariant = await this.prepareVariantInput(variant, productId, productInput.options);
        
        if (!preparedVariant) {
          console.error(`Failed to prepare variant input for individual variant creation`);
          continue;
        }
        
        // Extract option values from the prepared variant
        let optionValues = [];
        if (preparedVariant.optionValues && Array.isArray(preparedVariant.optionValues)) {
          // Ensure each option value has the required fields
          optionValues = preparedVariant.optionValues.map((value, index) => {
            // If the value is already in the correct format, use it
            if (typeof value === 'object' && value.name && value.optionName) {
              return value;
            }
            
            // If the value is a string, convert it to an object
            const optionValue = typeof value === 'string' ? value : (value.name || value.value || '');
            
            // Use the option names from actualOptionNames, then product input, then fallback
            const optionName = actualOptionNames[index] || 
                              (productInput.options && productInput.options[index] ? 
                                productInput.options[index].name : 
                                `Option${index + 1}`);
            
            return {
              name: optionValue,
              optionName: optionName
            };
          });
          console.log(`Using option values from prepared variant: ${JSON.stringify(optionValues)}`);
        }
        
        // Add inventory quantities if available
        let inventoryQuantity = 0;
        if (preparedVariant.inventoryQuantities && Array.isArray(preparedVariant.inventoryQuantities) && 
            preparedVariant.inventoryQuantities.length > 0) {
          inventoryQuantity = preparedVariant.inventoryQuantities[0].availableQuantity || 0;
        } else if (preparedVariant.inventoryQuantity !== undefined) {
          inventoryQuantity = preparedVariant.inventoryQuantity;
        }
        
        // Get location ID
        const locationId = await this.getShopifyLocationId();
        
        // Ensure SKU is set
        const sku = preparedVariant.sku || (preparedVariant.inventoryItem && preparedVariant.inventoryItem.sku) || '';
        
        console.log(`Creating individual variant with option values: ${JSON.stringify(optionValues)}, price: ${preparedVariant.price || '0.00'}, SKU: ${sku}`);
        
        // Create the variant
        const result = await this.createVariant(
          productId,
          optionValues,
          preparedVariant.price || '0.00',
          preparedVariant.compareAtPrice || null,
          inventoryQuantity,
          sku,
          locationId,
          productInput.options
        );
        
        if (result && result.productVariant) {
          console.log(`Successfully created individual variant with ID: ${result.productVariant.id}`);
          createdVariants.push(result.productVariant);
        } else {
          console.error(`Failed to create individual variant: ${JSON.stringify(result)}`);
        }
      } catch (error) {
        console.error(`Error creating individual variant: ${error.message}`);
      }
    }
    
    console.log(`Created ${createdVariants.length} variants individually`);
    return { variants: createdVariants };
  }

  /**
   * Creates product variants in chunks to avoid API limits
   * @param {string} productId - Shopify product ID
   * @param {Array} variants - Variants to create
   * @param {Object} productInput - Product input data
   * @returns {Promise<Array>} Created variants
   */
  async createProductVariantsInChunks(productId, variants, productInput = {}) {
    console.log(`Creating ${variants.length} variants for product ${productId}`);
    
    // Get actual option names directly from the product
    const actualOptionNames = await this.getProductOptionNames(productId);
    console.log(`Retrieved actual option names from product: ${JSON.stringify(actualOptionNames)}`);
    
    // Get option names from product input as fallback
    const productInputOptionNames = [];
    if (productInput && productInput.options && Array.isArray(productInput.options)) {
      for (let i = 0; i < productInput.options.length; i++) {
        if (productInput.options[i] && productInput.options[i].name) {
          productInputOptionNames[i] = productInput.options[i].name;
        }
      }
    }
    
    // Create a mapping for case-insensitive option value matching
    const optionValueMappings = [];
    
    if (productInput.options && Array.isArray(productInput.options)) {
      for (let i = 0; i < productInput.options.length; i++) {
        const option = productInput.options[i];
        if (option && option.values && Array.isArray(option.values)) {
          const mapping = option.values.map(value => {
            const normalizedValue = typeof value === 'string' ? value : (value.value || value.name || '');
            return [normalizedValue.toLowerCase(), normalizedValue];
          });
          optionValueMappings.push(mapping);
          console.log(`Created case mapping for option ${option.name} in createProductVariantsInChunks: ${JSON.stringify(mapping)}`);
        }
      }
    }
    
    // Combine option names, prioritizing the ones from the API
    const combinedOptionNames = [...actualOptionNames];
    for (let i = 0; i < productInputOptionNames.length; i++) {
      if (!combinedOptionNames[i] && productInputOptionNames[i]) {
        combinedOptionNames[i] = productInputOptionNames[i];
      }
    }
    console.log(`Combined option names for variant creation: ${JSON.stringify(combinedOptionNames)}`);
    
    // Validate that variants is an array
    if (!Array.isArray(variants)) {
      console.error('createProductVariantsInChunks was called with a non-array variants parameter');
      console.log('Variants:', variants);
      return { variants: [] };
    }
    
    // Ensure variants have option values
    const preparedVariants = variants.map(variant => {
      // Skip non-object variants
      if (!variant || typeof variant !== 'object') {
        console.error('Invalid variant object found in variants array');
        return null;
      }
      
      const preparedVariant = { ...variant };
      
      // Ensure optionValues is an array
      if (!preparedVariant.optionValues || !Array.isArray(preparedVariant.optionValues)) {
        console.log(`Variant missing optionValues in createProductVariantsInChunks, attempting to extract from options or selectedOptions`);
        
        // Try to extract from options array
        if (preparedVariant.options && Array.isArray(preparedVariant.options)) {
          preparedVariant.optionValues = preparedVariant.options.map((opt, index) => {
            const value = typeof opt === 'string' ? opt : (opt.value || opt.name || 'Default');
            const optionName = productInput.options && productInput.options[index] ? 
              productInput.options[index].name : 
              `Option${index + 1}`;
            
            return {
              name: value,
              optionName: optionName
            };
          });
          console.log(`Extracted optionValues from options array: ${JSON.stringify(preparedVariant.optionValues)}`);
        }
        // Try to extract from selectedOptions
        else if (preparedVariant.selectedOptions && Array.isArray(preparedVariant.selectedOptions)) {
          preparedVariant.optionValues = preparedVariant.selectedOptions.map((opt, index) => {
            const value = typeof opt === 'string' ? opt : (opt.value || 'Default');
            const optionName = productInput.options && productInput.options[index] ? 
              productInput.options[index].name : 
              `Option${index + 1}`;
            
            return {
              name: value,
              optionName: optionName
            };
          });
          console.log(`Extracted optionValues from selectedOptions array: ${JSON.stringify(preparedVariant.optionValues)}`);
        }
        // If we still don't have option values but we have product options, create default values
        else if (productInput.options && Array.isArray(productInput.options)) {
          preparedVariant.optionValues = productInput.options.map((option, index) => {
            let value = 'Default';
            if (option.values && option.values.length > 0) {
              value = typeof option.values[0] === 'string' ? option.values[0] : 
                     (option.values[0].name || option.values[0].value || 'Default');
            }
            
            return {
              name: value,
              optionName: option.name
            };
          });
          console.log(`Created default optionValues from product options: ${JSON.stringify(preparedVariant.optionValues)}`);
        }
      }
      
      // Ensure SKU is set properly
      if (!preparedVariant.sku && preparedVariant.inventoryItem && preparedVariant.inventoryItem.sku) {
        preparedVariant.sku = preparedVariant.inventoryItem.sku;
      }
      
      // Ensure price and compareAtPrice are properly formatted
      if (preparedVariant.price && typeof preparedVariant.price !== 'string') {
        preparedVariant.price = String(preparedVariant.price);
      }
      
      // Handle compareAtPrice from different possible sources
      let compareAtPrice = preparedVariant.compare_at_price || preparedVariant.compareAtPrice || null;
      if (compareAtPrice && typeof compareAtPrice !== 'string') {
        compareAtPrice = String(compareAtPrice);
      }
      if (compareAtPrice === '') {
        compareAtPrice = null;
      }
      preparedVariant.compareAtPrice = compareAtPrice;
      console.log(`Prepared variant with price: ${preparedVariant.price} and compareAtPrice: ${preparedVariant.compareAtPrice}`);
      
      return preparedVariant;
    }).filter(variant => variant !== null); // Filter out any null variants
    
    // Prepare variant inputs
    console.log('Preparing variant inputs for ' + preparedVariants.length + ' variants');
    const variantInputs = [];
    
    for (let i = 0; i < preparedVariants.length; i++) {
      try {
        const variant = preparedVariants[i];
        
        // Use prepareVariantInput to ensure consistent formatting
        const variantInput = await this.prepareVariantInput(variant, productId, combinedOptionNames.map(name => ({ name })));
        
        // Ensure option values use the correct option names
        if (variantInput.optionValues && Array.isArray(variantInput.optionValues)) {
          variantInput.optionValues = variantInput.optionValues.map((ov, index) => {
            return {
              name: ov.name,
              optionName: combinedOptionNames[index] || `Option${index + 1}`
            };
          });
        }
        
        variantInputs.push(variantInput);
      } catch (error) {
        console.error(`Error preparing variant input for variant ${i}:`, error);
      }
    }
    
    console.log(`Prepared ${variantInputs.length} variant inputs for API submission`);
    
    // Create variants in chunks of 10
    const results = [];
    const chunkSize = 10;
    
    for (let i = 0; i < variantInputs.length; i += chunkSize) {
      const chunk = variantInputs.slice(i, i + chunkSize);
      console.log(`Processing chunk ${Math.floor(i / chunkSize) + 1} with ${chunk.length} variants`);
      
      try {
        // Use the bulk variant creation method
        const result = await this.createProductVariants(productId, chunk, productInput);
        
        if (result && result.variants) {
          console.log(`Successfully created ${result.variants.length} variants in chunk ${Math.floor(i / chunkSize) + 1}`);
          results.push(...result.variants);
        } else {
          console.error(`Failed to create variants in chunk ${Math.floor(i / chunkSize) + 1}`);
          
          // Try to create variants individually as a fallback
          console.log(`Falling back to individual creation for chunk ${Math.floor(i / chunkSize) + 1}`);
          const individualResults = await this._createVariantsIndividually(productId, chunk, productInput);
          
          if (individualResults && individualResults.variants) {
            console.log(`Successfully created ${individualResults.variants.length} variants individually in chunk ${Math.floor(i / chunkSize) + 1}`);
            results.push(...individualResults.variants);
          }
        }
      } catch (error) {
        console.error(`Error creating variant chunk ${Math.floor(i / chunkSize) + 1}:`, error);
        
        // Try to create variants individually as a fallback
        try {
          console.log(`Attempting to create variants individually after chunk error in chunk ${Math.floor(i / chunkSize) + 1}`);
          const individualResults = await this._createVariantsIndividually(productId, chunk, productInput);
          
          if (individualResults && individualResults.variants) {
            console.log(`Successfully created ${individualResults.variants.length} variants individually after chunk error in chunk ${Math.floor(i / chunkSize) + 1}`);
            results.push(...individualResults.variants);
          }
        } catch (individualError) {
          console.error(`Error creating variants individually after chunk error: ${individualError.message}`);
        }
      }
    }
    
    console.log(`Successfully created ${results.length} variants in total`);
    return { variants: results };
  }

  /**
   * Create a single variant for a product
   * @param {string} productId - The ID of the product to create the variant for
   * @param {Array} optionValues - The option values for the variant
   * @param {string} price - The price of the variant
   * @param {string} compareAtPrice - The compare at price of the variant
   * @param {number} inventoryQuantity - The inventory quantity of the variant
   * @param {string} sku - The SKU of the variant
   * @param {string} locationId - The location ID for the inventory
   * @param {Array} productOptions - Product options
   * @returns {Promise<Object>} - The created variant
   */
  async createVariant(productId, optionValues, price, compareAtPrice, inventoryQuantity, sku, locationId, productOptions = []) {
    console.log(`Creating variant for product ${productId} with options ${JSON.stringify(optionValues)}`);
    
    // Get the actual option names directly from the product
    const actualOptionNames = await this.getProductOptionNames(productId);
    console.log(`Retrieved actual option names for single variant creation: ${JSON.stringify(actualOptionNames)}`);
    
    // Get option names from product options as fallback
    const productOptionNames = [];
    if (productOptions && Array.isArray(productOptions)) {
      for (let i = 0; i < productOptions.length; i++) {
        if (productOptions[i] && productOptions[i].name) {
          productOptionNames[i] = productOptions[i].name;
        }
      }
    }
    
    // Create a key from the option values to use for caching
    const optionKey = this._createOptionKey(optionValues);
    
    // Ensure option values are in the correct format for the API
    let formattedOptionValues = optionValues;
    
    // If option values are objects with name/value properties, extract just the values
    if (Array.isArray(optionValues) && optionValues.length > 0 && typeof optionValues[0] === 'object') {
      formattedOptionValues = optionValues.map(ov => {
        if (typeof ov === 'string') return ov;
        return ov.value || ov.name || 'Default';
      });
      console.log(`Converted option values objects to strings: ${JSON.stringify(formattedOptionValues)}`);
    }
    
    // Ensure we have at least one option value
    if (!formattedOptionValues || formattedOptionValues.length === 0) {
      formattedOptionValues = ['Default'];
      console.log(`Using fallback default option value: ${JSON.stringify(formattedOptionValues)}`);
    }
    
    // Format option values as required by the API
    const optionValuesInput = formattedOptionValues.map((value, index) => {
      // If it's already in the correct format, use it directly
      if (typeof value === 'object' && (value.name || value.value) && (value.optionId || value.optionName)) {
        // Check if we should update the option name to match the product options
        if (actualOptionNames[index]) {
          return {
            name: value.name || value.value,
            optionName: actualOptionNames[index]
          };
        }
        return value;
      }
      
      // Use the correct option names based on position
      const optionName = actualOptionNames[index] || 
                         (productOptions && productOptions[index] ? 
                           productOptions[index].name : 
                           `Option${index + 1}`);
      
      return {
        name: value,
        optionName: optionName
      };
    });
    
    // Prepare the input for the API request
    const variantInput = {
      optionValues: optionValuesInput,
      price: price || '0.00',
      compareAtPrice: compareAtPrice || null,
      inventoryQuantities: [
        {
          availableQuantity: inventoryQuantity,
          locationId: locationId
        }
      ],
      inventoryItem: {
        sku: sku // Set SKU directly in the variant input
      }
    };
    
    console.log(`Variant input for API: ${JSON.stringify(variantInput)}`);
    
    // Make the API request
    const shopifyService = this.shopifyService || this.serviceContainer.get('ShopifyGraphQLService');
    
    try {
      // Use the bulk variant creation method
      const response = await shopifyService.makeGraphQLRequest({
        query: `
          mutation productVariantsBulkCreate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
            productVariantsBulkCreate(productId: $productId, variants: $variants) {
              productVariants {
                id
                title
                price
                compareAtPrice
                inventoryQuantity
                selectedOptions {
                  name
                  value
                }
                inventoryItem {
                  id
                  sku
                }
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        variables: { 
          productId,
          variants: [variantInput]
        }
      });
      
      console.log('Variant creation response:', JSON.stringify(response));
      
      // Check for errors
      if (response.productVariantsBulkCreate.userErrors && 
          response.productVariantsBulkCreate.userErrors.length > 0) {
        console.error(`Error creating variant: ${JSON.stringify(response.productVariantsBulkCreate.userErrors)}`);
        return { error: response.productVariantsBulkCreate.userErrors };
      }
      
      // Return the first variant since we're only creating one
      if (response.productVariantsBulkCreate.productVariants && 
          response.productVariantsBulkCreate.productVariants.length > 0) {
        return { productVariant: response.productVariantsBulkCreate.productVariants[0] };
      } else {
        return { error: [{ message: 'No variant returned from API' }] };
      }
    } catch (error) {
      console.error(`Error in createVariant: ${error.message}`);
      return { error: [{ message: error.message }] };
    }
  }

  /**
   * Create a key from variant option values
   * @param {Object} variant - The variant object
   * @param {Array} options - Product options
   * @returns {string} - Key based on option values
   * @private
   */
  _createVariantKey(variant, options) {
    // Try to create a key from option1, option2, option3
    if (variant.option1) {
      const values = [];
      
      if (variant.option1) values.push(`${options[0]?.name || 'Option 1'}:${variant.option1}`);
      if (variant.option2) values.push(`${options[1]?.name || 'Option 2'}:${variant.option2}`);
      if (variant.option3) values.push(`${options[2]?.name || 'Option 3'}:${variant.option3}`);
      
      return values.join('|');
    }
    
    // Try to create a key from options array
    if (variant.options && Array.isArray(variant.options) && variant.options.length > 0) {
      return this._createVariantKeyFromOptionValues(variant.options);
    }
    
    // If we can't create a key, return null
    return null;
  }
  
  /**
   * Creates a key from option values array
   * @param {Array} optionValues - Array of option values
   * @returns {string} - Key based on option values
   * @private
   */
  _createVariantKeyFromOptionValues(optionValues) {
    if (!optionValues || !Array.isArray(optionValues) || optionValues.length === 0) {
      return null;
    }
    
    // For string array format (as required by Shopify API)
    if (typeof optionValues[0] === 'string') {
      return optionValues.map((value, index) => {
        return `Option${index + 1}:${value}`;
      }).join('|');
    }
    
    // For object format (legacy)
    return optionValues.map(opt => {
      const name = typeof opt === 'string' ? opt : opt.name || '';
      const optionName = typeof opt === 'string' ? 'Option' : opt.optionName || 'Option';
      return `${optionName}:${name}`;
    }).join('|');
  }

  /**
   * Process product options to ensure they have the correct structure
   * @param {Array} options - The product options
   * @param {Object} productInput - The product input data
   * @returns {Array} Processed options
   */
  processProductOptions(options, productInput = {}) {
    if (!options || !Array.isArray(options) || options.length === 0) {
      console.warn('No options provided, returning empty array');
      return [];
    }
    
    console.log(`Processing ${options.length} product options`);
    
    // Ensure each option has the required properties
    const processedOptions = options.map(option => {
      // Make sure the option has a name
      if (!option.name) {
        console.warn('Option missing name, using default');
        option.name = 'Option';
      }
      
      // Make sure the option has values
      if (!option.values || !Array.isArray(option.values)) {
        console.warn(`Option ${option.name} missing values, using empty array`);
        option.values = [];
      }
      
      // Make sure each value has a name property
      option.values = option.values.map(value => {
        if (typeof value === 'string') {
          return { name: value };
        }
        
        if (!value.name && value.value) {
          return { name: value.value };
        }
        
        return value;
      });
      
      return option;
    });
    
    return processedOptions;
  }

  /**
   * Associates images with variants based on SKUs
   * @param {Array} images - The images to associate
   * @param {Array} variants - The variants to associate with
   * @param {Object} skuChanges - Map of old SKUs to new SKUs
   * @returns {Object} Map of image IDs to variant IDs
   */
  associateImagesWithVariants(images, variants, skuChanges = {}) {
    console.log(`Associating ${images.length} images with ${variants.length} variants`);
    
    // Create a map of SKUs to variant IDs
    const skuMap = new Map();
    variants.forEach(variant => {
      const sku = variant.sku || (variant.inventoryItem && variant.inventoryItem.sku);
      if (sku) {
        skuMap.set(sku, variant.id);
      }
    });
    
    // Create a map of option combinations to variant IDs
    const variantMap = new Map();
    variants.forEach(variant => {
      if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
        const optionCombo = variant.selectedOptions
          .map(option => option.value)
          .sort()
          .join('_');
        variantMap.set(optionCombo, variant.id);
      }
    });
    
    // Map images to variants
    const imageVariantMap = {};
    
    images.forEach(image => {
      const variantIds = new Set();
      
      // Case 1: Image has explicit variant associations from the original product
      if (image.variantIds && image.variantIds.length > 0) {
        console.log(`Image has ${image.variantIds.length} explicit variant associations`);
        variantIds.add(...image.variantIds);
        console.log(`Using explicit variant IDs: ${[...variantIds].join(', ')}`);
      }
      
      // Case 2: Image has variant option combinations
      else if (image.variantOptions && image.variantOptions.length > 0) {
        console.log(`Image has ${image.variantOptions.length} variant option combinations`);
        
        // Convert variant option combinations to actual variant IDs using the variantMap
        for (const optionCombo of image.variantOptions) {
          const variantId = variantMap.get(optionCombo);
          if (variantId) {
            variantIds.add(variantId);
            console.log(`Mapped option combination '${optionCombo}' to variant ID ${variantId}`);
          } else {
            console.warn(`Could not find variant ID for option combination: ${optionCombo}`);
          }
        }
      }
      
      // Case 3: Image has a SKU association
      else if (image.sku && skuMap.has(image.sku)) {
        // Get variant ID if available
        const variantId = skuMap.get(image.sku);
        variantIds.add(variantId);
        console.log(`Associated image with variant ID ${variantId} based on SKU ${image.sku}`);
        
        // Check if this SKU was changed to a new one - safely access skuChanges
        const newSku = skuChanges[image.sku];
        if (newSku) {
          console.log(`Found SKU change mapping: ${image.sku} -> ${newSku}`);
          
          if (skuMap.has(newSku)) {
            const newVariantId = skuMap.get(newSku);
            variantIds.add(newVariantId);
            console.log(`Associating image with variant ${newVariantId} (matched by changed SKU ${image.sku} -> ${newSku})`);
          } else {
            console.log(`New SKU ${newSku} not found in skuMap, cannot associate image`);
          }
        }
      }
      
      // Store the associations
      if (variantIds.size > 0 && image.id) {
        imageVariantMap[image.id] = [...variantIds];
        console.log(`Associated image ${image.id} with variants: ${[...variantIds].join(', ')}`);
      }
    });
    
    return imageVariantMap;
  }

  /**
   * Get the Shopify location ID for inventory management
   * @param {Object} options - Options for getting the location ID
   * @returns {Promise<string>} - The Shopify location ID
   */
  async getShopifyLocationId(options = {}) {
    try {
      console.log('Getting Shopify location ID');
      
      // Check if we already have a cached location ID
      if (this.cachedLocationId) {
        console.log(`Using cached location ID: ${this.cachedLocationId}`);
        return this.cachedLocationId;
      }
      
      // Check if a location ID was provided in the options
      if (options.locationId) {
        console.log(`Using provided location ID from options: ${options.locationId}`);
        this.cachedLocationId = options.locationId;
        return options.locationId;
      }
      
      // Get the shopify service - either from this instance, options, or the service container
      const shopifyService = this.shopifyService || 
                            options.shopifyService || 
                            (this.serviceContainer ? this.serviceContainer.get('ShopifyGraphQLService') : null);
      
      if (!shopifyService) {
        console.error('No Shopify service available to get location ID, using fallback');
        // Use a fallback approach - try to get the location ID from inventory service or product service
        return this._getFallbackLocationId(options);
      }
      
      const query = `
        query {
          locations(first: 10) {
            edges {
              node {
                id
                name
                isActive
              }
            }
          }
        }
      `;
      
      const response = await shopifyService.makeGraphQLRequest({
        query,
        variables: {}
      });
      
      console.log('Location response:', JSON.stringify(response));
      
      if (response && response.locations && response.locations.edges && response.locations.edges.length > 0) {
        // Find the first active location
        const activeLocation = response.locations.edges.find(edge => 
          edge.node && edge.node.isActive
        );
        
        if (activeLocation && activeLocation.node && activeLocation.node.id) {
          console.log(`Found active location: ${activeLocation.node.name} (${activeLocation.node.id})`);
          this.cachedLocationId = activeLocation.node.id;
          return activeLocation.node.id;
        }
        
        // If no active location found, use the first one
        if (response.locations.edges[0].node && response.locations.edges[0].node.id) {
          console.log(`No active location found, using first location: ${response.locations.edges[0].node.name} (${response.locations.edges[0].node.id})`);
          this.cachedLocationId = response.locations.edges[0].node.id;
          return response.locations.edges[0].node.id;
        }
      }
      
      console.error('No Shopify locations found in response, using fallback');
      return this._getFallbackLocationId(options);
    } catch (error) {
      console.error(`Error getting Shopify location ID: ${error.message}, using fallback`);
      return this._getFallbackLocationId(options);
    }
  }
  
  /**
   * Get a fallback location ID when the API call fails
   * @param {Object} options - Options for getting the location ID
   * @returns {string} - A fallback location ID
   * @private
   */
  _getFallbackLocationId(options = {}) {
    // Try to get the location ID from other services if available
    try {
      if (this.serviceContainer) {
        // Try to get from inventory service
        const inventoryService = this.serviceContainer.get('InventoryService');
        if (inventoryService && inventoryService.getDefaultLocationId) {
          const locationId = inventoryService.getDefaultLocationId();
          if (locationId) {
            console.log(`Using location ID from inventory service: ${locationId}`);
            this.cachedLocationId = locationId;
            return locationId;
          }
        }
        
        // Try to get from product service
        const productService = this.serviceContainer.get('ShopifyProductService');
        if (productService && productService.getDefaultLocationId) {
          const locationId = productService.getDefaultLocationId();
          if (locationId) {
            console.log(`Using location ID from product service: ${locationId}`);
            this.cachedLocationId = locationId;
            return locationId;
          }
        }
      }
    } catch (error) {
      console.error(`Error getting fallback location ID: ${error.message}`);
    }
    
    // Last resort - use a hardcoded fallback that will be replaced on first successful API call
    console.warn('Using temporary fallback location ID - this will be replaced on first successful API call');
    return 'gid://shopify/Location/1'; // Generic fallback that will be replaced on first successful API call
  }

  /**
   * Get the product option names directly from the product
   * @param {string} productId - Shopify product ID
   * @returns {Promise<Array>} - The product option names
   */
  async getProductOptionNames(productId) {
    const shopifyService = this.shopifyService || this.serviceContainer.get('ShopifyGraphQLService');
    
    if (!shopifyService) {
      console.error('No Shopify service available to get product option names');
      return [];
    }
    
    const query = `
      query {
        product(id: "${productId}") {
          options {
            name
          }
        }
      }
    `;
    
    try {
      const response = await shopifyService.makeGraphQLRequest({
        query,
        variables: {}
      });
      
      if (response && response.product && response.product.options) {
        const optionNames = response.product.options.map(option => option.name);
        console.log(`Successfully retrieved option names from product: ${JSON.stringify(optionNames)}`);
        return optionNames;
      }
      
      console.error('Error getting product option names:', JSON.stringify(response));
      return [];
    } catch (error) {
      console.error(`Error getting product option names: ${error.message}`);
      return [];
    }
  }
}
